import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Save,
  FileText,
  BookOpen,
  Target,
  Lightbulb,
  Calendar,
  Users,
  Tag,
  Palette,
  Paperclip,
  Bell,
  Star,
  Pin,
  Archive,
  Plus,
  Minus,
  Upload,
  Link,
  Image as ImageIcon,
  Mic
} from 'lucide-react';
import { createNoteAsync, updateNoteAsync } from '../../store/slices/notesSlice';
import { Note, Subject } from '../../types';
import { RootState } from '../../store';

interface ModernNoteCreatorProps {
  onClose: () => void;
  folderId?: string;
  editNote?: Note;
  mode?: 'create' | 'edit';
}

type NoteType = 'general' | 'study' | 'exam' | 'project' | 'meeting' | 'idea';

interface NoteTemplate {
  type: NoteType;
  title: string;
  icon: React.ReactNode;
  description: string;
  fields: {
    id: string;
    label: string;
    type: 'text' | 'textarea' | 'select' | 'date' | 'tags' | 'list';
    placeholder?: string;
    options?: string[];
    required?: boolean;
  }[];
  defaultContent: string;
}

const noteTemplates: NoteTemplate[] = [
  {
    type: 'general',
    title: 'Genel Not',
    icon: <FileText className="w-5 h-5" />,
    description: 'Serbest format not',
    fields: [
      { id: 'title', label: 'Başlık', type: 'text', placeholder: 'Not başlığı...', required: true },
      { id: 'content', label: 'İçerik', type: 'textarea', placeholder: 'Not içeriği...' }
    ],
    defaultContent: ''
  },
  {
    type: 'study',
    title: 'Ders Notu',
    icon: <BookOpen className="w-5 h-5" />,
    description: 'Ders çalışma notları',
    fields: [
      { id: 'title', label: 'Konu Başlığı', type: 'text', placeholder: 'Ders konusu...', required: true },
      { id: 'subject', label: 'Ders', type: 'select', options: ['Matematik', 'Türkçe', 'Fen Bilimleri', 'İngilizce', 'Sosyal Bilgiler'] },
      { id: 'keyPoints', label: 'Önemli Noktalar', type: 'list', placeholder: 'Önemli nokta ekle...' },
      { id: 'formulas', label: 'Formüller', type: 'list', placeholder: 'Formül ekle...' },
      { id: 'examples', label: 'Örnekler', type: 'textarea', placeholder: 'Örnekler ve açıklamalar...' },
      { id: 'summary', label: 'Özet', type: 'textarea', placeholder: 'Konu özeti...' }
    ],
    defaultContent: '# {title}\n\n## Önemli Noktalar\n{keyPoints}\n\n## Formüller\n{formulas}\n\n## Örnekler\n{examples}\n\n## Özet\n{summary}'
  },
  {
    type: 'exam',
    title: 'Sınav Notu',
    icon: <Target className="w-5 h-5" />,
    description: 'Sınav hazırlık notları',
    fields: [
      { id: 'title', label: 'Sınav Adı', type: 'text', placeholder: 'Sınav adı...', required: true },
      { id: 'examDate', label: 'Sınav Tarihi', type: 'date' },
      { id: 'subjects', label: 'Konular', type: 'list', placeholder: 'Sınav konusu ekle...' },
      { id: 'studyPlan', label: 'Çalışma Planı', type: 'textarea', placeholder: 'Çalışma planı...' },
      { id: 'resources', label: 'Kaynaklar', type: 'list', placeholder: 'Kaynak ekle...' },
      { id: 'notes', label: 'Notlar', type: 'textarea', placeholder: 'Ek notlar...' }
    ],
    defaultContent: '# {title}\n\n**Sınav Tarihi:** {examDate}\n\n## Konular\n{subjects}\n\n## Çalışma Planı\n{studyPlan}\n\n## Kaynaklar\n{resources}\n\n## Notlar\n{notes}'
  },
  {
    type: 'project',
    title: 'Proje Notu',
    icon: <Users className="w-5 h-5" />,
    description: 'Proje yönetimi notları',
    fields: [
      { id: 'title', label: 'Proje Adı', type: 'text', placeholder: 'Proje adı...', required: true },
      { id: 'deadline', label: 'Teslim Tarihi', type: 'date' },
      { id: 'objectives', label: 'Hedefler', type: 'list', placeholder: 'Hedef ekle...' },
      { id: 'tasks', label: 'Görevler', type: 'list', placeholder: 'Görev ekle...' },
      { id: 'team', label: 'Takım Üyeleri', type: 'list', placeholder: 'Üye ekle...' },
      { id: 'progress', label: 'İlerleme', type: 'textarea', placeholder: 'Proje ilerlemesi...' }
    ],
    defaultContent: '# {title}\n\n**Teslim Tarihi:** {deadline}\n\n## Hedefler\n{objectives}\n\n## Görevler\n{tasks}\n\n## Takım\n{team}\n\n## İlerleme\n{progress}'
  },
  {
    type: 'meeting',
    title: 'Toplantı Notu',
    icon: <Calendar className="w-5 h-5" />,
    description: 'Toplantı kayıtları',
    fields: [
      { id: 'title', label: 'Toplantı Konusu', type: 'text', placeholder: 'Toplantı konusu...', required: true },
      { id: 'date', label: 'Tarih', type: 'date' },
      { id: 'participants', label: 'Katılımcılar', type: 'list', placeholder: 'Katılımcı ekle...' },
      { id: 'agenda', label: 'Gündem', type: 'list', placeholder: 'Gündem maddesi ekle...' },
      { id: 'decisions', label: 'Kararlar', type: 'list', placeholder: 'Karar ekle...' },
      { id: 'actionItems', label: 'Aksiyon Maddeleri', type: 'list', placeholder: 'Aksiyon maddesi ekle...' }
    ],
    defaultContent: '# {title}\n\n**Tarih:** {date}\n\n## Katılımcılar\n{participants}\n\n## Gündem\n{agenda}\n\n## Kararlar\n{decisions}\n\n## Aksiyon Maddeleri\n{actionItems}'
  },
  {
    type: 'idea',
    title: 'Fikir Notu',
    icon: <Lightbulb className="w-5 h-5" />,
    description: 'Yaratıcı fikirler',
    fields: [
      { id: 'title', label: 'Fikir Başlığı', type: 'text', placeholder: 'Fikir başlığı...', required: true },
      { id: 'description', label: 'Açıklama', type: 'textarea', placeholder: 'Fikir açıklaması...' },
      { id: 'benefits', label: 'Faydalar', type: 'list', placeholder: 'Fayda ekle...' },
      { id: 'challenges', label: 'Zorluklar', type: 'list', placeholder: 'Zorluk ekle...' },
      { id: 'nextSteps', label: 'Sonraki Adımlar', type: 'list', placeholder: 'Adım ekle...' },
      { id: 'inspiration', label: 'İlham Kaynağı', type: 'textarea', placeholder: 'İlham kaynağı...' }
    ],
    defaultContent: '# {title}\n\n## Açıklama\n{description}\n\n## Faydalar\n{benefits}\n\n## Zorluklar\n{challenges}\n\n## Sonraki Adımlar\n{nextSteps}\n\n## İlham Kaynağı\n{inspiration}'
  }
];

const colors = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6B7280'
];

const ModernNoteCreator: React.FC<ModernNoteCreatorProps> = ({ onClose, folderId, editNote, mode = 'create' }) => {
  // Early return if in edit mode but no note provided
  if (mode === 'edit' && !editNote) {
    console.error('Edit mode requires editNote prop');
    onClose();
    return null;
  }
  const dispatch = useAppDispatch();
  const [selectedType, setSelectedType] = useState<NoteType>('general');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [color, setColor] = useState(colors[0]);
  const [isPinned, setIsPinned] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [reminders, setReminders] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Parse note content to extract field values
  const parseNoteContent = (content: string, noteType: string) => {
    const template = noteTemplates.find(t => t.type === noteType);
    if (!template) return { title: '', content };

    const parsedData: Record<string, any> = {};
    
    // Extract title from first line if it starts with #
    const lines = content.split('\n');
    if (lines[0]?.startsWith('# ')) {
      parsedData.title = lines[0].replace('# ', '').trim();
    }

    // For general notes, just use the content as is
    if (noteType === 'general') {
      return {
        title: parsedData.title || '',
        content: parsedData.title ? lines.slice(1).join('\n').trim() : content
      };
    }

    // Parse structured content for other note types
    template.fields.forEach(field => {
      if (field.id === 'title') {
        parsedData[field.id] = parsedData.title || '';
      } else if (field.type === 'list') {
        // Extract list items from sections
        const sectionRegex = new RegExp(`## ${field.label}\\s*\\n([\\s\\S]*?)(?=\\n## |$)`, 'i');
        const match = content.match(sectionRegex);
        if (match) {
          const listItems = match[1]
            .split('\n')
            .map(line => line.replace(/^[-*]\s*/, '').trim())
            .filter(line => line.length > 0);
          parsedData[field.id] = listItems;
        } else {
          parsedData[field.id] = [];
        }
      } else {
        // Extract text content from sections
        const sectionRegex = new RegExp(`## ${field.label}\\s*\\n([\\s\\S]*?)(?=\\n## |$)`, 'i');
        const match = content.match(sectionRegex);
        parsedData[field.id] = match ? match[1].trim() : '';
      }
    });

    return parsedData;
  };

  // Initialize form data for edit mode
  useEffect(() => {
    if (mode === 'edit' && editNote) {
      const noteType = editNote.noteType || 'general';
      setSelectedType(noteType);
      
      // Parse the content to extract field values
      const parsedData = parseNoteContent(editNote.content, noteType);
      setFormData(parsedData);
      
      setTags(editNote.tags || []);
      setColor(editNote.color || colors[0]);
      setIsPinned(editNote.isPinned || false);
      setIsFavorite(editNote.isFavorite || false);
      setAttachments(editNote.attachments || []);
      setReminders(editNote.reminders || []);
    }
  }, [mode, editNote]);

  const selectedTemplate = noteTemplates.find(t => t.type === selectedType)!;

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleListAdd = (fieldId: string, value: string) => {
    if (!value.trim()) return;
    const currentList = formData[fieldId] || [];
    setFormData(prev => ({
      ...prev,
      [fieldId]: [...currentList, value.trim()]
    }));
  };

  const handleListRemove = (fieldId: string, index: number) => {
    const currentList = formData[fieldId] || [];
    setFormData(prev => ({
      ...prev,
      [fieldId]: currentList.filter((_: any, i: number) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const generateContent = () => {
    let content = selectedTemplate.defaultContent;
    
    // Replace placeholders with actual data
    Object.entries(formData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const listContent = value.map(item => `- ${item}`).join('\n');
        content = content.replace(`{${key}}`, listContent);
      } else {
        content = content.replace(`{${key}}`, value || '');
      }
    });
    
    return content;
  };

  const handleSave = async () => {
    const title = formData.title || 'Başlıksız Not';
    console.log('handleSave çağrıldı', { title, formData, folderId, mode, editNote });
    
    if (!title.trim()) {
      alert('Lütfen not başlığını girin');
      return;
    }

    setIsLoading(true);
    
    // Generate content based on template and form data
    let content = '';
    if (selectedType === 'general') {
      content = formData.content || '';
    } else {
      content = generateContent();
    }
    
    console.log('İçerik oluşturuldu:', content);
    
    try {
      if (mode === 'edit' && editNote && editNote.id) {
        // Update existing note
        console.log('Not güncelleniyor:', editNote.id);
        const updatedNote: any = {
          ...editNote,
          title: title.trim(),
          content,
          noteType: selectedType,
          tags,
          color,
          isPinned,
          isFavorite,
          attachments,
          reminders,
          updatedAt: new Date().toISOString()
        };
        
        // Sadece geçerli değerleri güncelle
        if (formData.subject) {
          updatedNote.subjectId = formData.subject;
        }
        if (folderId) {
          updatedNote.folderId = folderId;
        }
        console.log('Güncellenecek not verisi:', updatedNote);
        const result = await dispatch(updateNoteAsync(updatedNote));
        if (updateNoteAsync.fulfilled.match(result)) {
          console.log('Not başarıyla güncellendi:', result.payload);
          onClose();
        } else {
          console.error('Not güncelleme hatası:', result.error);
        }
      } else {
        // Create new note
        console.log('Yeni not oluşturuluyor');
        const noteData: any = {
          title: title.trim(),
          content,
          noteType: selectedType,
          tags,
          color,
          isPinned,
          isArchived: false,
          isFavorite,
          attachments,
          reminders,
          collaborators: []
        };
        
        // Sadece geçerli değerleri ekle
        if (formData.subject) {
          noteData.subjectId = formData.subject;
        }
        if (folderId) {
          noteData.folderId = folderId;
        }
        console.log('Oluşturulacak not verisi:', noteData);
        const result = await dispatch(createNoteAsync(noteData));
        if (createNoteAsync.fulfilled.match(result)) {
          console.log('Not başarıyla oluşturuldu:', result.payload);
          onClose();
        } else {
          console.error('Not oluşturma hatası:', result.error);
        }
      }
    } catch (error) {
      console.error('Not işlemi sırasında hata:', error);
      alert('Not kaydedilirken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const renderField = (field: any) => {
    const value = formData[field.id] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            required={field.required}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          >
            <option value="">Seçiniz...</option>
            {field.options?.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
        );

      case 'list':
        const listItems = formData[field.id] || [];
        return (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={field.placeholder}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleListAdd(field.id, e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  const input = document.querySelector(`input[placeholder="${field.placeholder}"]`) as HTMLInputElement;
                  if (input) {
                    handleListAdd(field.id, input.value);
                    input.value = '';
                  }
                }}
                className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {listItems.length > 0 && (
              <div className="space-y-1">
                {listItems.map((item: string, index: number) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="text-sm">{item}</span>
                    <button
                      type="button"
                      onClick={() => handleListRemove(field.id, index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'edit' ? 'Not Düzenle' : 'Yeni Not Oluştur'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Sidebar - Note Types */}
          <div className="w-80 border-r border-gray-100 p-6 overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Not Türü Seçin</h3>
            <div className="space-y-2">
              {noteTemplates.map((template) => (
                <button
                  key={template.type}
                  onClick={() => setSelectedType(template.type)}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${
                    selectedType === template.type
                      ? 'bg-blue-50 border-2 border-blue-500 text-blue-900'
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      selectedType === template.type ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {template.icon}
                    </div>
                    <span className="font-medium">{template.title}</span>
                  </div>
                  <p className="text-sm text-gray-600">{template.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Template Fields */}
              {selectedTemplate.fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}

              {/* Tags */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <Tag className="w-4 h-4 inline mr-2" />
                  Etiketler
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Etiket ekle..."
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Ekle
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="hover:text-blue-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Color and Options */}
              <div className="grid grid-cols-2 gap-6">
                {/* Color Picker */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Palette className="w-4 h-4 inline mr-2" />
                    Renk
                  </label>
                  <div className="flex gap-2">
                    {colors.map((c) => (
                      <button
                        key={c}
                        onClick={() => setColor(c)}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          color === c ? 'border-gray-400 scale-110' : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Seçenekler</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isPinned}
                        onChange={(e) => setIsPinned(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Pin className="w-4 h-4" />
                      <span className="text-sm">Sabitle</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isFavorite}
                        onChange={(e) => setIsFavorite(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Star className="w-4 h-4" />
                      <span className="text-sm">Favorilere ekle</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
              <Paperclip className="w-4 h-4" />
              Dosya Ekle
            </button>
            <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
              <Bell className="w-4 h-4" />
              Hatırlatıcı
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !formData.title?.trim()}
              className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-4 h-4" />
              {isLoading ? 'Kaydediliyor...' : (mode === 'edit' ? 'Güncelle' : 'Kaydet')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ModernNoteCreator;